import { createBrowserRouter } from 'react-router';

import { PageLoader } from '@/components/feedback/PageLoader';
import { RouteErrorBoundary } from '@/components/feedback/RouteErrorBoundary';
import { RequireAuth } from '@/features/auth';
import { AppShell } from '@/layouts/AppShell';

/** Every route is `lazy` so each page ships as its own chunk; the shell is
 *  eager. Route modules export a `Component` and stay thin.
 *
 *  The tree is split by access, not by layout: the auth screens sit outside the
 *  shell because a director on them has no session, and rendering navigation to
 *  places they cannot go is worse than rendering none. */
export const router = createBrowserRouter([
  {
    errorElement: <RouteErrorBoundary />,
    // Every child route is `lazy`, so the first paint always waits on a chunk.
    // Without this the data router warns and renders nothing during that gap.
    HydrateFallback: PageLoader,
    children: [
      // Public. Reachable with no session, by design.
      { path: 'login/signing/:token', lazy: () => import('@/routes/login') },
      { path: 'u/:token', lazy: () => import('@/routes/short-link') },
      { path: 'otp', lazy: () => import('@/routes/otp') },
      { path: 'locked', lazy: () => import('@/routes/locked') },
      { path: 'logout', lazy: () => import('@/routes/logout') },
      { path: 'error', lazy: () => import('@/routes/error') },
      { path: 'maintenance', lazy: () => import('@/routes/maintenance') },

      // Protected. The guard runs before the shell so an expired session never
      // paints the chrome on its way to redirecting.
      {
        element: <RequireAuth />,
        children: [
          {
            path: '/',
            element: <AppShell />,
            children: [
              { index: true, lazy: () => import('@/routes/dashboard') },
              { path: 'companies', lazy: () => import('@/routes/companies') },
              {
                path: 'companies/:asicCompanyId',
                lazy: () => import('@/routes/company-detail'),
              },
              { path: 'settings', lazy: () => import('@/routes/settings') },
              { path: '*', lazy: () => import('@/routes/not-found') },
            ],
          },
        ],
      },
    ],
  },
]);
